import React, { useContext, useState } from 'react';
import {
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
} from '@material-ui/core';
import { DatePicker } from '../Controls/DatePicker';
import { ApplicationPriceSelector } from './ApplicationPriceSelector/ApplicationPriceSelector';
import { DownlineAgencySelector } from '../DownlineAgencySelector/DownlineAgencySelector';
import { Input } from '../Controls/MaskedInput';
import { EmergencyForm } from '../ApplicationForm/EmergencyForm';
import { FuneralHomeForm } from '../ApplicationForm/FuneralHomeForm';
import { AddressForm } from './AddressForm';
import InputLabel from './InputLabel/InputLabel';
import { UserContext } from '../../context/user';
import { FORM_CONFIG } from './constants';

import cn from './ApplicationForm.module.scss';

export const ApplicationForm = React.memo(
  ({ agencyNonce, value, onChange }) => {
    const [agencyId, setAgencyId] = useState();
    const user = useContext(UserContext);

    const handleDownlineChange = (agency) => {
      if (agency) {
        setAgencyId(agency.id);
        handleChange({
          target: { name: FORM_CONFIG.member_agent.key, value: agency.id },
        });
      }
    };

    const handleChange = (event) => {
      const { name, value: newValue } = event.target;
      onChange({
        ...value,
        errors: { ...value.errors, [name]: null },
        form: { ...value.form, [name]: newValue },
      });
    };

    const handleAddressChange = (form) => {
      const address = {
        [FORM_CONFIG.member_country.key]: form.country,
        [FORM_CONFIG.member_state_or_province.key]: form.state,
        [FORM_CONFIG.member_current_legal_address.key]: form.address,
        [FORM_CONFIG.member_city.key]: form.city,
        [FORM_CONFIG.member_zipcode.key]: form.zipcode,
      };

      const errorsToClear = Object.keys(address)
        .filter((name) => Boolean(address[name]))
        .reduce((res, name) => ({ ...res, [name]: null }), {});
      onChange({
        ...value,
        errors: {
          ...value.errors,
          ...errorsToClear,
        },
        form: {
          ...value.form,
          ...address,
        },
      });
    };

    const handleDateChange = (date, name) => {
      handleChange({ target: { name, value: date } });
    };

    const handlePriceSelectorChange = (form) => {
      onChange({
        ...value,
        errors: {
          ...value.errors,
          [FORM_CONFIG.member_travel_assurence_plan_price.key]: null,
          [FORM_CONFIG.member_travel_assurence_plan_months.key]: null,
          [FORM_CONFIG.member_travel_assurence_plan.key]: null,
        },
        form: { ...value.form, ...form },
      });
    };

    const selectedAgencyId = agencyNonce
      ? null
      : agencyId || (user ? user.id : undefined);

    return (
      <div className={cn.form}>
        {!!user && !agencyNonce && (
          <div className={cn.field}>
            <InputLabel text="Apasi Agency ID:" />
            <DownlineAgencySelector
              onChange={handleDownlineChange}
              defaultValue={user}
            />
          </div>
        )}
        <div className={cn.row}>
          <div className={cn.field}>
            <InputLabel
              text="First Name:"
              required={FORM_CONFIG.member_firstname.required}
              error={value.errors[FORM_CONFIG.member_firstname.key]}
            />
            <TextField
              value={value.form[FORM_CONFIG.member_firstname.key]}
              variant="outlined"
              name={FORM_CONFIG.member_firstname.key}
              fullWidth
              onChange={handleChange}
              error={!!value.errors[FORM_CONFIG.member_firstname.key]}
            />
          </div>
          <div className={cn.field}>
            <InputLabel
              text="Last Name:"
              required={FORM_CONFIG.member_lastname.required}
              error={value.errors[FORM_CONFIG.member_lastname.key]}
            />
            <TextField
              value={value.form[FORM_CONFIG.member_lastname.key]}
              variant="outlined"
              name={FORM_CONFIG.member_lastname.key}
              fullWidth
              onChange={handleChange}
              error={!!value.errors[FORM_CONFIG.member_lastname.key]}
            />
          </div>
        </div>

        <AddressForm
          values={{
            country: {
              value: value.form[FORM_CONFIG.member_country.key],
              error: value.errors[FORM_CONFIG.member_country.key],
              required: FORM_CONFIG.member_country.required,
            },
            state: {
              value: value.form[FORM_CONFIG.member_state_or_province.key],
              error: value.errors[FORM_CONFIG.member_state_or_province.key],
              required: FORM_CONFIG.member_state_or_province.required,
            },
            address: {
              value: value.form[FORM_CONFIG.member_current_legal_address.key],
              error: value.errors[FORM_CONFIG.member_current_legal_address.key],
              required: FORM_CONFIG.member_current_legal_address.required,
            },
            city: {
              value: value.form[FORM_CONFIG.member_city.key],
              error: value.errors[FORM_CONFIG.member_city.key],
              required: FORM_CONFIG.member_city.required,
            },
            zipcode: {
              value: value.form[FORM_CONFIG.member_zipcode.key],
              error: value.errors[FORM_CONFIG.member_zipcode.key],
              required: FORM_CONFIG.member_zipcode.required,
            },
          }}
          onChange={handleAddressChange}
        />

        <div className={cn.field}>
          <InputLabel
            text="Date of Birth:"
            required={FORM_CONFIG.member_dob.required}
            error={value.errors[FORM_CONFIG.member_dob.key]}
          />
          <DatePicker
            value={value.form[FORM_CONFIG.member_dob.key] || null}
            onChange={(date) =>
              handleDateChange(date, FORM_CONFIG.member_dob.key)
            }
            inputVariant="outlined"
            variant="inline"
            disableFuture
            error={!!value.errors[FORM_CONFIG.member_dob.key]}
            allowKeyboardInput
          />
        </div>
        <div className={cn.field}>
          <InputLabel
            text={'Gender:'}
            required={FORM_CONFIG.member_sex.required}
            error={value.errors[FORM_CONFIG.member_sex.key]}
          />
          <RadioGroup
            value={value.form[FORM_CONFIG.member_sex.key]}
            onChange={handleChange}
            name={FORM_CONFIG.member_sex.key}
            row
          >
            {FORM_CONFIG.member_sex.options.map((option) => (
              <FormControlLabel
                key={option.value}
                control={<Radio color="primary" />}
                label={option.label}
                value={option.value}
              />
            ))}
          </RadioGroup>
        </div>
        <div className={cn.row}>
          <div className={cn.field}>
            <InputLabel
              text="Last 4 of SSN:"
              required={FORM_CONFIG.member_social_security_number.required}
              error={
                value.errors[FORM_CONFIG.member_social_security_number.key]
              }
            />
            <TextField
              value={value.form[FORM_CONFIG.member_social_security_number.key]}
              variant="outlined"
              name={FORM_CONFIG.member_social_security_number.key}
              fullWidth
              onChange={handleChange}
              inputProps={{
                maxlength: 4,
              }}
              error={
                !!value.errors[FORM_CONFIG.member_social_security_number.key]
              }
            />
          </div>
          <div className={cn.field}>
            <InputLabel
              text="Cell Phone:"
              required={FORM_CONFIG.member_home_phone_number.required}
              error={value.errors[FORM_CONFIG.member_home_phone_number.key]}
            />
            <Input
              value={value.form[FORM_CONFIG.member_home_phone_number.key]}
              variant="outlined"
              name={FORM_CONFIG.member_home_phone_number.key}
              fullWidth
              onChange={handleChange}
              mask="999-999-9999"
              error={!!value.errors[FORM_CONFIG.member_home_phone_number.key]}
            />
          </div>
          <div className={cn.field}>
            <InputLabel
              text="Email Address:"
              required={FORM_CONFIG.member_email.required}
              error={value.errors[FORM_CONFIG.member_email.key]}
            />
            <TextField
              value={value.form[FORM_CONFIG.member_email.key]}
              variant="outlined"
              name={FORM_CONFIG.member_email.key}
              fullWidth
              onChange={handleChange}
              error={!!value.errors[FORM_CONFIG.member_email.key]}
            />
          </div>
        </div>
        <div className={cn.field}>
          <InputLabel
            text="Select a Plan of Assistance:"
            required={FORM_CONFIG.member_travel_assurence_plan.required}
            error={value.errors[FORM_CONFIG.member_travel_assurence_plan.key]}
          />
          <RadioGroup
            value={value.form[FORM_CONFIG.member_travel_assurence_plan.key]}
            onChange={handleChange}
            name={FORM_CONFIG.member_travel_assurence_plan.key}
            row
          >
            {FORM_CONFIG.member_travel_assurence_plan.options.map((option) => (
              <FormControlLabel
                key={option.value}
                control={<Radio color="primary" />}
                label={option.label}
                value={option.value}
              />
            ))}
          </RadioGroup>
        </div>
        <ApplicationPriceSelector
          application={value}
          agencyId={selectedAgencyId}
          agencyNonce={agencyNonce}
          onChange={handlePriceSelectorChange}
        />
        <div className={cn.subForms}>
          <EmergencyForm
            onChange={handleChange}
            className={cn.subForm}
            value={value}
          />
          <FuneralHomeForm
            onChange={handleChange}
            className={cn.subForm}
            value={value}
          />
        </div>
      </div>
    );
  },
);
